"use client"

import { useState } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  RibbonDivider,
  RibbonGroup,
  RibbonIconButton,
  RibbonToolbar,
} from "@/components/editor/ribbon/ribbon-primitives"
import { useRibbonActions } from "@/components/editor/ribbon/use-ribbon-actions"

const R = "/ribbion_menu"

function MarginField({
  label,
  icon,
  value,
  onChange,
  onCommit,
}: {
  label: string
  icon: React.ReactNode
  value: number
  onChange: (n: number) => void
  onCommit: (n: number) => void
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="flex h-5 w-5 items-center justify-center text-[#6B7280]" aria-hidden>
        {icon}
      </span>
      <span className="w-9 shrink-0 text-[10px] text-[#4B5563]">{label}</span>
      <div className="flex h-6 items-stretch overflow-hidden rounded border border-[var(--ed-ribbon-group-divider)] bg-[var(--ed-ribbon-field-bg)]">
        <input
          type="number"
          min={0}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          onBlur={() => onCommit(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommit(value)
          }}
          className="w-[42px] bg-transparent px-1 text-[11px] text-[var(--ed-ribbon-field-text)] outline-none"
          aria-label={label}
        />
        <span className="flex items-center pr-0.5 text-[9px] text-[#9CA3AF]">px</span>
        <div className="flex flex-col border-l border-[var(--ed-ribbon-group-divider)]">
          <button
            type="button"
            className="flex h-3 w-3.5 items-center justify-center hover:bg-[#F3F4F6]"
            aria-label={`Increase ${label}`}
            onClick={() => {
              const next = value + 1
              onChange(next)
              onCommit(next)
            }}
          >
            <ChevronUp className="h-2.5 w-2.5" />
          </button>
          <button
            type="button"
            className="flex h-3 w-3.5 items-center justify-center hover:bg-[#F3F4F6]"
            aria-label={`Decrease ${label}`}
            onClick={() => {
              const next = Math.max(0, value - 1)
              onChange(next)
              onCommit(next)
            }}
          >
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
        </div>
      </div>
    </div>
  )
}

function LeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <rect x="2" y="2" width="10" height="10" rx="1" stroke="#6B7280" strokeWidth="1.2" />
      <path d="M4 2V12" stroke="#6B7280" strokeWidth="1.4" />
    </svg>
  )
}
function RightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <rect x="2" y="2" width="10" height="10" rx="1" stroke="#6B7280" strokeWidth="1.2" />
      <path d="M10 2V12" stroke="#6B7280" strokeWidth="1.4" />
    </svg>
  )
}
function TopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <rect x="2" y="2" width="10" height="10" rx="1" stroke="#6B7280" strokeWidth="1.2" />
      <path d="M2 4H12" stroke="#6B7280" strokeWidth="1.4" />
    </svg>
  )
}
function BottomIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <rect x="2" y="2" width="10" height="10" rx="1" stroke="#6B7280" strokeWidth="1.2" />
      <path d="M2 10H12" stroke="#6B7280" strokeWidth="1.4" />
    </svg>
  )
}

export function LayoutToolbar() {
  const actions = useRibbonActions()
  const [left, setLeft] = useState(0)
  const [right, setRight] = useState(0)
  const [top, setTop] = useState(0)
  const [bottom, setBottom] = useState(0)
  const [landscape, setLandscape] = useState(true)

  return (
    <RibbonToolbar>
      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/marginsIcon.svg`}
          hoverHint={`${R}/marginsHover.svg`}
          label="Margins"
          onClick={actions.margins}
        />
        <RibbonIconButton
          icon={`${R}/orientationIcon.svg`}
          hoverHint={`${R}/orientationHover.svg`}
          label="Orientation"
          onClick={() => {
            const next = !landscape
            setLandscape(next)
            actions.orientation(next)
          }}
        />
        <RibbonIconButton
          icon={`${R}/sizeIcon.svg`}
          hoverHint={`${R}/sizeHover.svg`}
          label="Size"
          onClick={actions.pageSize}
        />
        <RibbonIconButton
          icon={`${R}/columnIcon.svg`}
          hoverHint={`${R}/columnHover.svg`}
          label="Columns"
          onClick={() => actions.columns(2)}
        />
        <RibbonIconButton
          icon={`${R}/breaksIcon.svg`}
          hoverHint={`${R}/breaksHover.svg`}
          label="Breaks"
          onClick={actions.breaks}
        />
        <RibbonIconButton
          icon={`${R}/lineNumbersIcon.svg`}
          hoverHint={`${R}/lineNumbersHover.svg`}
          label="Line Numbers"
          onClick={actions.lineNumbers}
        />
      </RibbonGroup>

      <RibbonDivider />

      <div
        className={cn(
          "grid shrink-0 grid-cols-2 gap-x-2 gap-y-1 rounded-md bg-[var(--ed-ribbon-group-bg)] px-1.5 py-1",
        )}
      >
        <MarginField
          label="Left:"
          icon={<LeftIcon />}
          value={left}
          onChange={setLeft}
          onCommit={(n) => actions.marginSide("left", n)}
        />
        <MarginField
          label="Top:"
          icon={<TopIcon />}
          value={top}
          onChange={setTop}
          onCommit={(n) => actions.marginSide("top", n)}
        />
        <MarginField
          label="Right:"
          icon={<RightIcon />}
          value={right}
          onChange={setRight}
          onCommit={(n) => actions.marginSide("right", n)}
        />
        <MarginField
          label="Bottom:"
          icon={<BottomIcon />}
          value={bottom}
          onChange={setBottom}
          onCommit={(n) => actions.marginSide("bottom", n)}
        />
      </div>

      <RibbonDivider />

      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/positionIcon.svg`}
          hoverHint={`${R}/positionHover.svg`}
          label="Position"
          onClick={actions.position}
        />
        <RibbonIconButton
          icon={`${R}/selectPanelIcon.svg`}
          hoverHint={`${R}/selectPanelHover.svg`}
          label="Selection Pane"
          onClick={actions.selectPanel}
        />
        <RibbonIconButton
          icon={`${R}/wrapTextIcon.svg`}
          hoverHint={`${R}/wrapTextHover.svg`}
          label="Wrap Text"
          onClick={actions.wrapText}
        />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup>
        <RibbonIconButton
          icon={`${R}/bringForwardIcon.svg`}
          hoverHint={`${R}/bringForwardHover.svg`}
          label="Bring Forward"
          onClick={actions.bringForward}
        />
        <RibbonIconButton
          icon={`${R}/bringbackwardIcon.svg`}
          hoverHint={`${R}/bringbackwardHover.svg`}
          label="Send Backward"
          onClick={actions.bringBackward}
        />
        <RibbonIconButton
          icon={`${R}/alignIcon.svg`}
          hoverHint={`${R}/alignHover.svg`}
          label="Align"
          onClick={actions.objectAlign}
        />
      </RibbonGroup>
    </RibbonToolbar>
  )
}
